<template>
  <div class="input-wrapper" :class="wrapperClass">
    <label v-if="label" :for="inputId" class="input-label">
      {{ label }}
      <span v-if="required" class="input-required">*</span>
    </label>

    <div class="input-container" :class="containerClass">
      <!-- Leading Icon -->
      <span v-if="$slots.icon" class="input-icon input-icon--leading">
        <slot name="icon"></slot>
      </span>

      <!-- Input Element -->
      <input
        :id="inputId"
        ref="input"
        class="input-field"
        :class="inputClass"
        :type="type"
        :value="value"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :required="required"
        v-bind="$attrs"
        @input="onInput"
        @focus="onFocus"
        @blur="onBlur"
      />

      <!-- Trailing Icon / Clear Button -->
      <span v-if="$slots['icon-trailing'] || (clearable && value)" class="input-icon input-icon--trailing">
        <button
          v-if="clearable && value"
          type="button"
          class="input-clear"
          @click="onClear"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        <slot v-else name="icon-trailing"></slot>
      </span>
    </div>

    <!-- Helper Text / Error Message -->
    <p v-if="error" class="input-error">{{ error }}</p>
    <p v-else-if="hint" class="input-hint">{{ hint }}</p>
  </div>
</template>

<script>
let inputIdCounter = 0

export default {
  name: 'BaseInput',

  inheritAttrs: false,

  props: {
    value: {
      type: [String, Number],
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    type: {
      type: String,
      default: 'text'
    },
    size: {
      type: String,
      default: 'md',
      validator: (value) => ['sm', 'md', 'lg'].includes(value)
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: ''
    },
    hint: {
      type: String,
      default: ''
    },
    clearable: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      inputId: `input-${++inputIdCounter}`,
      isFocused: false
    }
  },

  computed: {
    wrapperClass() {
      return {
        'input-wrapper--disabled': this.disabled,
        'input-wrapper--error': !!this.error
      }
    },
    containerClass() {
      return [
        `input-container--${this.size}`,
        {
          'input-container--focused': this.isFocused,
          'input-container--error': !!this.error,
          'input-container--disabled': this.disabled,
          'input-container--has-leading': !!this.$slots.icon,
          'input-container--has-trailing': !!this.$slots['icon-trailing'] || (this.clearable && this.value)
        }
      ]
    },
    inputClass() {
      return [
        `input-field--${this.size}`,
        {
          'input-field--has-leading': !!this.$slots.icon,
          'input-field--has-trailing': !!this.$slots['icon-trailing'] || (this.clearable && this.value)
        }
      ]
    }
  },

  methods: {
    onInput(event) {
      this.$emit('input', event.target.value)
    },
    onFocus(event) {
      this.isFocused = true
      this.$emit('focus', event)
    },
    onBlur(event) {
      this.isFocused = false
      this.$emit('blur', event)
    },
    onClear() {
      this.$emit('input', '')
      this.$emit('clear')
      this.$refs.input.focus()
    },
    focus() {
      this.$refs.input.focus()
    }
  }
}
</script>

<style scoped>
.input-wrapper {
  display: flex;
  flex-direction: column;
  gap: var(--space-1, 0.25rem);
}

/* Label */
.input-label {
  display: block;
  font-size: var(--text-base, 0.8125rem);
  font-weight: var(--font-medium, 500);
  color: var(--text-primary, #18181b);
  margin-bottom: var(--space-1, 0.25rem);
}

.input-required {
  color: var(--color-error-500, #ef4444);
  margin-left: 2px;
}

/* Container */
.input-container {
  position: relative;
  display: flex;
  align-items: center;
  background: var(--bg-card, #ffffff);
  border: var(--input-border, 1px solid #e4e4e7);
  border-radius: var(--input-radius, 0.5rem);
  transition: border-color var(--transition-base, 0.2s), box-shadow var(--transition-base, 0.2s);
}

.input-container--focused {
  border-color: var(--border-color-focus, #0ea5e9);
  box-shadow: var(--input-focus-ring, 0 0 0 3px rgba(14, 165, 233, 0.1));
}

.input-container--error {
  border-color: var(--color-error-500, #ef4444);
}

.input-container--error.input-container--focused {
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

.input-container--disabled {
  background: var(--bg-muted, #f4f4f5);
  cursor: not-allowed;
}

/* Sizes */
.input-container--sm {
  min-height: 32px;
}

.input-container--md {
  min-height: 40px;
}

.input-container--lg {
  min-height: 48px;
}

/* Input Field */
.input-field {
  flex: 1;
  width: 100%;
  padding: var(--input-padding-y, 0.625rem) var(--input-padding-x, 0.875rem);
  font-size: var(--text-md, 0.875rem);
  color: var(--text-primary, #18181b);
  background: transparent;
  border: none;
  outline: none;
}

.input-field::placeholder {
  color: var(--text-disabled, #a1a1aa);
}

.input-field:disabled {
  cursor: not-allowed;
  color: var(--text-muted, #71717a);
}

/* Field Sizes */
.input-field--sm {
  padding: 0.375rem 0.75rem;
  font-size: var(--text-base, 0.8125rem);
}

.input-field--lg {
  padding: 0.75rem 1rem;
  font-size: var(--text-lg, 0.9375rem);
}

/* With Icons */
.input-field--has-leading {
  padding-left: var(--space-2, 0.5rem);
}

.input-field--has-trailing {
  padding-right: var(--space-2, 0.5rem);
}

/* Icons */
.input-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-muted, #71717a);
  flex-shrink: 0;
}

.input-icon svg {
  width: var(--icon-md, 1.25rem);
  height: var(--icon-md, 1.25rem);
}

.input-icon--leading {
  padding-left: var(--space-3, 0.75rem);
}

.input-icon--trailing {
  padding-right: var(--space-3, 0.75rem);
}

/* Clear Button */
.input-clear {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  background: var(--bg-muted, #f4f4f5);
  border: none;
  border-radius: var(--radius-full, 50%);
  color: var(--text-muted, #71717a);
  cursor: pointer;
  transition: all var(--transition-base, 0.2s);
}

.input-clear:hover {
  background: var(--color-gray-200, #e4e4e7);
  color: var(--text-primary, #18181b);
}

.input-clear svg {
  width: 12px;
  height: 12px;
}

/* Helper Text */
.input-hint,
.input-error {
  font-size: var(--text-sm, 0.75rem);
  margin: var(--space-1, 0.25rem) 0 0 0;
}

.input-hint {
  color: var(--text-muted, #71717a);
}

.input-error {
  color: var(--color-error-500, #ef4444);
}
</style>
